// Hộp thoại khách ký điện tử: chọn mẫu → xem nội dung đã điền sẵn → khách tick đồng ý + ký → lưu bản chụp nội dung, chữ ký, mã xác thực
import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Modal, Select, message } from 'antd';
import { FileSignature, Printer } from 'lucide-react';
import { BRANCHES } from '../POS/data';
import usePosStore from '../POS/store';
import type { TreatmentCard } from '../Treatment/data';
import { useTreatmentAuth } from '../Treatment/actions';
import { SIGN_KIND, type SignDoc, type SignKind } from './data';
import useC360Store from './store';
import SignaturePad from './SignaturePad';
import { CONSENT_QUESTIONS, buildSections, defaultTitle, printDoc, type ConsentAnswers, type SignParty } from './templates';

export interface SignTarget { kind: SignKind; cardId?: string; usageId?: string }

interface Props {
  target: SignTarget | null;
  party: SignParty;
  customerKey: string;
  cards: TreatmentCard[];
  onClose: () => void;
  onSigned?: (doc: SignDoc) => void;
}

export default function SignModal({ target, party, customerKey, cards, onClose, onSigned }: Props) {
  const sign = useC360Store(s => s.sign);
  const markPrinted = useC360Store(s => s.markPrinted);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { userName } = useTreatmentAuth();
  const [kind, setKind] = useState<SignKind>('consent');
  const [cardId, setCardId] = useState<string>('');
  const [usageId, setUsageId] = useState<string>('');
  const [answers, setAnswers] = useState<ConsentAnswers>({});
  const [agree, setAgree] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [witness, setWitness] = useState('');
  const [branchId, setBranchId] = useState(workingBranch);
  const [signature, setSignature] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [padKey, setPadKey] = useState(0);

  useEffect(() => {
    if (!target) return;
    setKind(target.kind);
    setCardId(target.cardId || cards.find(c => c.status !== 'cancelled')?.id || '');
    setUsageId(target.usageId || '');
    setAnswers({});
    setAgree(false);
    setSignerName(party.name);
    setWitness(userName);
    setBranchId(workingBranch);
    setSignature(null);
    setPadKey(k => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const card = cards.find(c => c.id === cardId) || null;
  const usage = card?.usages.find(u => u.id === usageId) || null;
  const needCard = kind === 'contract' || kind === 'session';
  const sections = useMemo(() => buildSections(kind, { party: { ...party, name: signerName || party.name }, card, usage, answers }), [kind, party, signerName, card, usage, answers]);

  const errors: string[] = [];
  if (needCard && !card) errors.push('Chọn thẻ liệu trình');
  if (kind === 'session' && !usage) errors.push('Chọn buổi cần xác nhận');
  if (!signerName.trim()) errors.push('Nhập tên người ký');
  if (!agree) errors.push('Khách cần tick xác nhận đã đọc');
  if (!signature) errors.push('Khách chưa ký');

  const submit = async (andPrint: boolean) => {
    if (errors.length || !signature) return;
    setSaving(true);
    try {
      const doc = await sign({
        kind, title: defaultTitle(kind, card), customerKey, posCustomerId: card?.customerId, cardId: needCard ? card?.id : undefined,
        usageId: kind === 'session' ? usage?.id : undefined, sections, signerName: signerName.trim(), signerPhone: party.phone,
        witness: witness.trim() || userName, branchId, signature,
      });
      message.success(`Đã lưu văn bản ký ${doc.id}`);
      if (andPrint) { printDoc(doc, signature); markPrinted(doc.id); }
      onSigned?.(doc);
      onClose();
    } catch (e) {
      message.error('Không lưu được chữ ký (trình duyệt chặn bộ nhớ?)');
    } finally {
      setSaving(false);
    }
  };

  const liveCards = cards.filter(c => c.status !== 'cancelled');
  const usageOptions = (card?.usages || []).filter(u => !u.cancelled).slice().reverse()
    .map(u => ({ value: u.id, label: `${new Date(u.at).toLocaleString('vi-VN')} · ${u.items.map(i => `${i.name} ×${i.qty}`).join(', ')}` }));

  return (
    <Modal open={!!target} onCancel={onClose} centered width={760} destroyOnHidden
      title={<span className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-[#357f96]" /> Khách ký điện tử</span>}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-red-500">{errors[0] || ''}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Hủy</button>
            <button onClick={() => submit(true)} disabled={!!errors.length || saving} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#357f96] px-4 text-sm font-semibold text-[#357f96] disabled:opacity-40"><Printer className="h-4 w-4" /> Lưu & in</button>
            <button onClick={() => submit(false)} disabled={!!errors.length || saving} className="h-9 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f] disabled:opacity-40">Lưu chữ ký</button>
          </div>
        </div>
      }>
      <div className="grid gap-3 text-sm">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">Loại văn bản</span>
            <Select className="w-full" value={kind} onChange={v => { setKind(v); setUsageId(''); }}
              options={(['consent', 'contract', 'photo'] as SignKind[]).map(k => ({ value: k, label: SIGN_KIND[k].label }))} />
          </label>
          {needCard && (
            <label className="block">
              <span className="mb-1 block font-medium text-slate-700">Thẻ liệu trình</span>
              <Select className="w-full" value={cardId || undefined} placeholder="Chọn thẻ" onChange={v => { setCardId(v); setUsageId(''); }}
                options={liveCards.map(c => ({ value: c.id, label: `${c.packageName} · ${c.id}` }))} notFoundContent="Khách chưa có thẻ" />
            </label>
          )}
          {kind === 'session' && (
            <label className="block sm:col-span-2">
              <span className="mb-1 block font-medium text-slate-700">Buổi cần xác nhận</span>
              <Select className="w-full" value={usageId || undefined} placeholder="Chọn buổi" onChange={setUsageId} options={usageOptions} notFoundContent="Thẻ chưa có buổi nào" />
            </label>
          )}
        </div>

        {kind === 'consent' && (
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 font-semibold text-slate-700">Khai báo sức khỏe</div>
            <div className="grid gap-2">
              {CONSENT_QUESTIONS.map(q => {
                const a = answers[q.key] || { yes: false, note: '' };
                return (
                  <div key={q.key} className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1">{q.label}</span>
                    <Select size="small" className="w-24" value={a.yes ? 'yes' : 'no'} onChange={v => setAnswers(s => ({ ...s, [q.key]: { ...a, yes: v === 'yes' } }))}
                      options={[{ value: 'no', label: 'Không' }, { value: 'yes', label: 'Có' }]} />
                    {a.yes && <Input size="small" className="!w-56" placeholder="Ghi rõ" value={a.note} onChange={e => setAnswers(s => ({ ...s, [q.key]: { ...a, note: e.target.value } }))} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-2 text-center text-base font-bold uppercase text-slate-800">{defaultTitle(kind, card)}</div>
          {sections.map(s => (
            <div key={s.heading} className="mb-2">
              <div className="font-semibold text-slate-700">{s.heading}</div>
              {s.lines.map((l, i) => <div key={i} className="text-slate-600">{l}</div>)}
            </div>
          ))}
        </div>

        <Checkbox checked={agree} onChange={e => setAgree(e.target.checked)}>
          <span className="font-medium">Tôi đã đọc, hiểu rõ và đồng ý với toàn bộ nội dung trên</span>
        </Checkbox>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block"><span className="mb-1 block font-medium text-slate-700">Người ký</span><Input value={signerName} onChange={e => setSignerName(e.target.value)} /></label>
          <label className="block"><span className="mb-1 block font-medium text-slate-700">Nhân viên chứng kiến</span><Input value={witness} onChange={e => setWitness(e.target.value)} /></label>
          <label className="block"><span className="mb-1 block font-medium text-slate-700">Ký tại</span><Select className="w-full" value={branchId} onChange={setBranchId} options={BRANCHES.map(b => ({ value: b.id, label: b.name }))} /></label>
        </div>

        <SignaturePad key={padKey} onChange={setSignature} />
        <p className="text-xs text-slate-500">Văn bản lưu kèm thời điểm ký, thiết bị và mã xác thực SHA-256 của nội dung + chữ ký. Sửa nội dung sau khi ký sẽ không khớp mã.</p>
      </div>
    </Modal>
  );
}
